/**
 * The page's side of caching: the service worker (serviceWorker.ts, built as `sw.js`), its updates,
 * pruning old files from IndexedDB, and clearing everything (the settings button).
 *
 * The worker is on in a build and off on the dev server (it would keep stale modules), unless the
 * URL says `?sw=1` (on) or `?sw=0` (off, and any worker of the game unregistered).
 */
import { assetStats } from './assets';
import { assetsManifest, baseHref } from './manifest';
import { refreshModules } from './staleModules';
import { assetStore } from './store';

const WORKER = 'sw.js';
/** How often an open game asks the server for a new build. */
const CHECK_EVERY_MS = 60 * 60_000;
/** Cache Storage names of the worker (serviceWorker.ts). */
const CACHE_PREFIX = 'nine-';

let updated = false;
const updateWatchers = new Set<() => void>();
/** Calls `f` once a new version of the game is ready (it runs from the next load). */
export function onAppUpdate(f: () => void): void {
  if (updated) f();
  else updateWatchers.add(f);
}
function announceUpdate(): void {
  if (updated) return;
  updated = true;
  for (const f of updateWatchers) f();
  updateWatchers.clear();
}

let stale = false;
const staleWatchers = new Set<() => void>();
/**
 * Calls `f` once a part of the game failed to load (a lazily loaded chunk, such as the 3D renderer):
 * most likely a new version replaced its files, and the page has to be loaded again.
 */
export function onReloadNeeded(f: () => void): void {
  if (stale) f();
  else staleWatchers.add(f);
}
function announceStale(): void {
  if (stale) return;
  stale = true;
  // On the dev server the browser's cached copy of the code may be what is stale: refreshed now,
  // so the reload the player is asked for loads the current one (staleModules.ts).
  void refreshModules();
  for (const f of staleWatchers) f();
  staleWatchers.clear();
}

/**
 * A lazily loaded chunk (`() => import('./render3d')`), or null when it cannot be loaded: the
 * failure is warned about and the player told to reload (`onReloadNeeded`), and the game carries on
 * without it.
 */
export async function loadChunk<T>(load: () => Promise<T>, what: string): Promise<T | null> {
  try {
    return await load();
  } catch (error) {
    console.warn(`Could not load ${what}: the game may have been updated`, error);
    announceStale();
    return null;
  }
}

/** Whether the worker should run here: `?sw=` decides, else on in a build and off on the dev server. */
function wanted(): boolean {
  const q = new URLSearchParams(location.search).get('sw');
  if (q === '1') return true;
  if (q === '0') return false;
  return !(typeof __NINE_DEV_SERVER__ !== 'undefined' && __NINE_DEV_SERVER__);
}

const workerUrl = (): string => new URL(WORKER, baseHref()).href;
const ours = (r: ServiceWorkerRegistration): boolean =>
  r.scope === baseHref() || [r.active, r.waiting, r.installing].some((w) => w?.scriptURL === workerUrl());

/** The page's entry script (its URL is hashed by the build): which version of the game this page is. */
function pageEntry(): string | null {
  return document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src ?? null;
}

function loaded(): Promise<void> {
  return document.readyState === 'complete'
    ? Promise.resolve()
    : new Promise((ok) => addEventListener('load', () => ok(), { once: true }));
}

/** Starts the service worker (or takes it away, see `wanted`), and prunes stale cached files. */
export function startCaching(): void {
  if (import.meta.env.DEV)
    Object.assign(window, {
      __assets: {
        stats: assetStats,
        usage: () => assetStore.usage(),
        estimate: () => navigator.storage?.estimate?.(),
      },
    });
  // A chunk that failed to load (Vite's preload helper says so) needs a reload.
  addEventListener('vite:preloadError', () => announceStale());
  // Files from older versions go once this page's manifest is known, after the load.
  void (async () => {
    const m = await assetsManifest();
    await loaded();
    if (m) await assetStore.prune(m.files);
  })();
  try {
    if (!('serviceWorker' in navigator) || !isSecureContext) return;
    const sw = navigator.serviceWorker;
    if (!wanted()) {
      void sw
        .getRegistrations()
        .then((rs) => Promise.all(rs.filter(ours).map((r) => r.unregister())))
        .catch(() => {});
      return;
    }
    void (async () => {
      // A new version left waiting by an earlier visit takes over now, if this load's page is
      // already the new one (pages are fetched network-first). Offline, the page may be the old one
      // from the cache: the worker checks that the page's entry script is in its own shell.
      const before = await sw.getRegistration(baseHref());
      before?.waiting?.postMessage({ type: 'skip-waiting', entry: pageEntry() });
      // Installing precaches the shell: not while the page itself is still loading.
      await loaded();
      const reg = await sw.register(workerUrl(), { scope: baseHref(), updateViaCache: 'none' });
      reg.addEventListener('updatefound', () => {
        const next = reg.installing;
        next?.addEventListener('statechange', () => {
          // Installed beside a worker already in charge: an update, not the first install.
          if (next.state === 'installed' && sw.controller) announceUpdate();
        });
      });
      if (reg.waiting && sw.controller) announceUpdate();
      setInterval(() => void reg.update().catch(() => {}), CHECK_EVERY_MS);
    })().catch((error: unknown) => console.warn('Service worker not started', error));
  } catch (error) {
    console.warn('Service worker not started', error);
  }
}

/** Clears every cached file of the game: IndexedDB and the worker's caches (the settings button). */
export async function clearGameCache(): Promise<void> {
  await assetStore.clear();
  try {
    for (const name of await caches.keys()) if (name.startsWith(CACHE_PREFIX)) await caches.delete(name);
  } catch {
    // No Cache Storage (an insecure page): nothing kept there.
  }
}
